import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

const KPIManagement = () => {
  const userMeta = localStorage.getItem('login_meta');
  const { companyCode, branchId } = userMeta ? JSON.parse(userMeta) : {};

  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);

  const [formData, setFormData] = useState({
    kpiName: '',
    kpiCode: '',
    description: '',
    category: 'SALES',
    measurementType: 'NUMERIC',
    unit: '',
    targetValue: 0,
    minimumValue: 0,
    maximumValue: 0,
    weightage: 10,
    evaluationFrequency: 'MONTHLY',
    applicableDepartments: [],
    applicableDesignations: []
  });

  const [filters, setFilters] = useState({
    category: '',
    isActive: true
  });

  const categories = [
    { value: 'SALES', label: 'Sales', color: '#4caf50' },
    { value: 'QUALITY', label: 'Quality', color: '#2196f3' },
    { value: 'EFFICIENCY', label: 'Efficiency', color: '#ff9800' },
    { value: 'CUSTOMER_SERVICE', label: 'Customer Service', color: '#9c27b0' },
    { value: 'ATTENDANCE', label: 'Attendance', color: '#f44336' },
    { value: 'TEAMWORK', label: 'Teamwork', color: '#00bcd4' },
    { value: 'LEADERSHIP', label: 'Leadership', color: '#ff5722' },
    { value: 'INNOVATION', label: 'Innovation', color: '#ffc107' },
    { value: 'OTHER', label: 'Other', color: '#607d8b' }
  ];

  const measurementTypes = [
    { value: 'NUMERIC', label: 'Numeric' },
    { value: 'PERCENTAGE', label: 'Percentage' },
    { value: 'RATING', label: 'Rating (1-5)' },
    { value: 'BOOLEAN', label: 'Yes/No' },
    { value: 'TEXT', label: 'Text' }
  ];

  const frequencies = [
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'HALF_YEARLY', label: 'Half Yearly' },
    { value: 'YEARLY', label: 'Yearly' }
  ];

  useEffect(() => {
    fetchKPIs();
  }, [filters]);

  const fetchKPIs = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/employee-performance/kpi/list', {
        ...filters,
        page: 1,
        limit: 100
      });
      if (response.data.success) {
        setKpis(response.data.data || []);
      } else {
        setKpis([]);
      }
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      setKpis([]); // Ensure kpis is always an array
      Swal.fire('Error', 'Failed to fetch KPIs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (kpi = null) => {
    if (kpi) {
      setEditMode(true);
      setSelectedKPI(kpi);
      setFormData({
        kpiName: kpi.kpiName,
        kpiCode: kpi.kpiCode,
        description: kpi.description,
        category: kpi.category,
        measurementType: kpi.measurementType,
        unit: kpi.unit,
        targetValue: kpi.targetValue,
        minimumValue: kpi.minimumValue,
        maximumValue: kpi.maximumValue,
        weightage: kpi.weightage,
        evaluationFrequency: kpi.evaluationFrequency,
        applicableDepartments: kpi.applicableDepartments || [],
        applicableDesignations: kpi.applicableDesignations || []
      });
    } else {
      setEditMode(false);
      setSelectedKPI(null);
      setFormData({
        kpiName: '',
        kpiCode: '',
        description: '',
        category: 'SALES',
        measurementType: 'NUMERIC',
        unit: '',
        targetValue: 0,
        minimumValue: 0,
        maximumValue: 0,
        weightage: 10,
        evaluationFrequency: 'MONTHLY',
        applicableDepartments: [],
        applicableDesignations: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedKPI(null);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.kpiName) {
      Swal.fire('Error', 'KPI name is required', 'error');
      return;
    }

    if (formData.weightage < 0 || formData.weightage > 100) {
      Swal.fire('Error', 'Weightage must be between 0 and 100', 'error');
      return;
    }

    try {
      let response;
      if (editMode) {
        response = await axios.put(`/api/employee-performance/kpi/${selectedKPI._id}`, formData);
      } else {
        response = await axios.post('/api/employee-performance/kpi/create', formData);
      }

      if (response.data.success) {
        Swal.fire('Success', `KPI ${editMode ? 'updated' : 'created'} successfully`, 'success');
        handleCloseDialog();
        fetchKPIs();
      }
    } catch (error) {
      console.error('Error saving KPI:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to save KPI', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete KPI?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Delete'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`/api/employee-performance/kpi/${id}`);
        if (response.data.success) {
          Swal.fire('Deleted', 'KPI deleted successfully', 'success');
          fetchKPIs();
        }
      } catch (error) {
        console.error('Error deleting KPI:', error);
        Swal.fire('Error', 'Failed to delete KPI', 'error');
      }
    }
  };

  const getCategoryColor = (category) => {
    return categories.find(c => c.value === category)?.color || '#757575';
  };

  const getCategoryLabel = (category) => {
    return categories.find(c => c.value === category)?.label || category;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          KPI Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create KPI
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total KPIs
              </Typography>
              <Typography variant="h4">{kpis?.length || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active KPIs
              </Typography>
              <Typography variant="h4" color="success.main">
                {kpis?.filter(k => k.isActive).length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Categories
              </Typography>
              <Typography variant="h4">
                {new Set(kpis.map(k => k.category)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Weightage
              </Typography>
              <Typography variant="h4">
                {kpis && kpis.length > 0 ? Math.round(kpis.reduce((sum, k) => sum + k.weightage, 0) / kpis.length) : 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="Category"
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.isActive}
              onChange={(e) => setFilters(prev => ({ ...prev, isActive: e.target.value }))}
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
              <MenuItem value="">All</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* KPI Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>KPI Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Measurement</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Weightage</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : !kpis || kpis.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No KPIs found
                </TableCell>
              </TableRow>
            ) : (
              (kpis || []).map((kpi) => (
                <TableRow key={kpi._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon sx={{ color: getCategoryColor(kpi.category) }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {kpi.kpiName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {kpi.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{kpi.kpiCode}</TableCell>
                  <TableCell>
                    <Chip
                      label={getCategoryLabel(kpi.category)}
                      size="small"
                      sx={{
                        bgcolor: getCategoryColor(kpi.category),
                        color: 'white'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {kpi.measurementType}
                    {kpi.unit && ` (${kpi.unit})`}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      Target: {kpi.targetValue}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Min: {kpi.minimumValue} | Max: {kpi.maximumValue}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${kpi.weightage}%`}
                      size="small"
                      color={kpi.weightage >= 30 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{kpi.evaluationFrequency}</TableCell>
                  <TableCell>
                    <Chip
                      label={kpi.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={kpi.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(kpi)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(kpi._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit KPI' : 'Create New KPI'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="KPI Name *"
                value={formData.kpiName}
                onChange={(e) => setFormData(prev => ({ ...prev, kpiName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="KPI Code"
                value={formData.kpiCode}
                onChange={(e) => setFormData(prev => ({ ...prev, kpiCode: e.target.value }))}
                placeholder="e.g., KPI-SALES-001"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Category *"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Measurement Type *"
                value={formData.measurementType}
                onChange={(e) => setFormData(prev => ({ ...prev, measurementType: e.target.value }))}
              >
                {measurementTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Unit"
                value={formData.unit}
                onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                placeholder="e.g., Sales, %, Count, Hours"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Evaluation Frequency *"
                value={formData.evaluationFrequency}
                onChange={(e) => setFormData(prev => ({ ...prev, evaluationFrequency: e.target.value }))}
              >
                {frequencies.map((freq) => (
                  <MenuItem key={freq.value} value={freq.value}>
                    {freq.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Value"
                value={formData.minimumValue}
                onChange={(e) => setFormData(prev => ({ ...prev, minimumValue: parseFloat(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Target Value *"
                value={formData.targetValue}
                onChange={(e) => setFormData(prev => ({ ...prev, targetValue: parseFloat(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Value"
                value={formData.maximumValue}
                onChange={(e) => setFormData(prev => ({ ...prev, maximumValue: parseFloat(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Weightage (%) *"
                value={formData.weightage}
                onChange={(e) => setFormData(prev => ({ ...prev, weightage: parseFloat(e.target.value) || 0 }))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                helperText="Weightage out of 100 for performance calculation"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KPIManagement;
